export const generateCSVDataAndHeaders = (productListData, initialColumns) => {
  const formatDate = (dateString) => {
    if (!dateString) return ""; // Handle empty/null values

    // Check if date contains a time component
    const hasTime = dateString.includes(":");

    // Convert date to a standard format that JavaScript can parse
    const formattedDateString = hasTime
      ? dateString.replace(" ", "T") // Convert "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ss"
      : `${dateString}T00:00:00`; // Add time for "YYYY-MM-DD" format

    const date = new Date(formattedDateString);

    if (isNaN(date.getTime())) return ""; // Handle invalid dates

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  // console.log("===productListDataproductListDataproductListData=====", initialColumns);

  // console.log("CSVData>>>", productListData);
  const CSVData = productListData.map((product) => ({
    name: product?.name || "",
    closing_inventory: product?.closing_inventory
      ? parseFloat(product.closing_inventory).toFixed(2)
      : "0",
    items_sold_per_day: product?.items_sold_per_day
      ? parseFloat(product?.items_sold_per_day).toFixed(2)
      : "0",
    items_sold: product?.items_sold
      ? parseFloat(product?.items_sold).toFixed(2)
      : "0",
    inbound_inventory: product?.inbound_inventory
      ? parseFloat(product?.inbound_inventory).toFixed(2)
      : "0",
    inventory_days_cover: product?.inventory_days_cover
      ? parseFloat(product.inventory_days_cover).toFixed(2)
      : "0",
    avg_cost: product?.avg_cost ? parseFloat(product.avg_cost).toFixed(2) : "0",
    brand: product?.brand || " ",
    vendor: product?.vendor || "",
    category: product?.category || "",
    gross_profit: product?.gross_profit
      ? parseFloat(product.gross_profit).toFixed(2)
      : "0",
    margin: product?.margin ? parseFloat(product.margin).toFixed(2) : "0",
    customer_count: product?.customer_count
      ? parseFloat(product?.customer_count).toFixed(2)
      : "0",
    sale_count: product?.sale_count
      ? parseFloat(product?.sale_count).toFixed(2)
      : "0",
    inventory_cost: product?.inventory_cost
      ? parseFloat(product.inventory_cost).toFixed(2)
      : "0",
    avg_items_per_sale: product?.avg_items_per_sale
      ? parseFloat(product?.avg_items_per_sale).toFixed(2)
      : "0",
    sale_discounted: product?.sale_discounted
      ? parseFloat(product.sale_discounted).toFixed(2)
      : "0",
    avg_sale_value: product?.avg_sale_value
      ? parseFloat(product.avg_sale_value).toFixed(2)
      : "0",
    cost_goods_sold: product?.cost_goods_sold
      ? parseFloat(product.cost_goods_sold).toFixed(2)
      : "0",
    retail_value: product?.retail_value
      ? parseFloat(product.retail_value).toFixed(2)
      : "0",
    start_date_inventory: product?.start_date_inventory
      ? parseFloat(product?.start_date_inventory).toFixed(2)
      : "0",
    reorder_point: product?.reorder_point
      ? parseFloat(product?.reorder_point).toFixed(2)
      : "0",
    reorder_amount: product?.reorder_amount
      ? parseFloat(product?.reorder_amount).toFixed(2)
      : "0",
    return_count: product?.return_count
      ? parseFloat(product?.return_count).toFixed(2)
      : "0",
    inventory_returns: product?.inventory_returns
      ? parseFloat(product?.inventory_returns).toFixed(2)
      : "0",
    sell_through_rate: product?.sell_through_rate
      ? `${parseFloat(product.sell_through_rate).toFixed(2)}%`
      : "0%",
    created: product?.created ? formatDate(product.created) : "",
    firstSale: product?.firstSale ? formatDate(product.firstSale) : "",
    first_sale: product?.first_sale ? formatDate(product.first_sale) : "",
    lastSale: product?.lastSale ? formatDate(product.lastSale) : "",
    last_sale: product?.last_sale ? formatDate(product.last_sale) : "",
    last_received: product?.last_received
      ? formatDate(product.last_received)
      : "",
    tag: product?.tag || "",
    current_inventory: product?.current_inventory
      ? parseFloat(product.current_inventory).toFixed(2)
      : "0",
    discount_value: product?.discount_value
      ? parseFloat(product?.discount_value).toFixed(2)
      : "0",
    net_sale: product?.net_sale ? parseFloat(product.net_sale).toFixed(2) : "0",
  }));

  const excludeFields = [
    "inbound_inventory",
    "inventory_days_cover",
    "avg_cost",
    "margin",
    "avg_items_per_sale",
    "sale_discounted",
    "avg_sale_value",
    "reorder_point",
    "reorder_amount",
    "sell_through_rate",
    "inventory_returns",
  ];

  const totalRow = CSVData.reduce(
    (acc, product) => {
      Object.keys(product).forEach((key) => {
        if (key === "name") {
          acc[key] = "Total"; // Ensure the name is "Total"
        } else if (
          !excludeFields.includes(key) &&
          !isNaN(product[key]) &&
          product[key] !== ""
        ) {
          acc[key] = (acc[key] || 0) + parseFloat(product[key]);
        } else if (!(key in acc)) {
          acc[key] = ""; // Keep excluded/non-numeric fields empty
        }
      });
      return acc;
    },
    { name: "Total" } // Initialize with "Total"
  );

  // Format only summed numeric values
  Object.keys(totalRow).forEach((key) => {
    if (!isNaN(totalRow[key]) && totalRow[key] !== "") {
      totalRow[key] = totalRow[key].toFixed(2);
    }
  });

  // Add the total row to CSVData
  CSVData.push(totalRow);

  const headers = [
    { id: "name", name: "Product name" },
    { id: "closing_inventory", name: "Closing Inventory" },
    { id: "items_sold_per_day", name: "Items Sold per Day" },
    { id: "items_sold", name: "Items Sold" },
    { id: "inbound_inventory", name: "Inbound Inventory" },
    { id: "inventory_days_cover", name: "Days Cover" },
    { id: "avg_cost", name: "Average Cost" },
    { id: "brand", name: "Brand" },
    { id: "vendor", name: "Vendor" },
    { id: "category", name: "Category" },
    { id: "gross_profit", name: "Gross Profit" },
    { id: "margin", name: "Margin" },
    { id: "customer_count", name: "Customer Count" },
    { id: "sale_count", name: "Sales Count" },
    { id: "inventory_cost", name: "Inventory Cost" },
    { id: "avg_items_per_sale", name: "Avg. Items per Sale" },
    { id: "sale_discounted", name: "Discount (%)" },
    { id: "avg_sale_value", name: "Avg. Sale Value" },
    { id: "cost_goods_sold", name: "Cost of Goods Sold" },
    { id: "retail_value", name: "Retail value (excl. tax)" },
    { id: "start_date_inventory", name: "Start Date Inventory" },
    { id: "reorder_point", name: "Reorder Point" },
    { id: "reorder_amount", name: "Restock amount" },
    { id: "return_count", name: "Return Count" },
    { id: "inventory_returns", name: "Returns (%)" },
    { id: "sell_through_rate", name: "Sell-Through Rate" },
    { id: "created", name: "Created" },
    { id: "firstSale", name: "First Sale" },
    { id: "lastSale", name: "Last Sale" },
    { id: "last_received", name: "Last Received" },
    { id: "tag", name: "Tag" },
    { id: "current_inventory", name: "Current Inventory" },
  ];

  const CSVHeaders = initialColumns.map((column) => ({
    label: column.name,
    key: column.id,
  }));

  return { CSVData, CSVHeaders };
};
